/**
 * @since 2026-04-21
 * @author Francesco Parrella
 * @maintainer Francesco Parrella
 * @copyright AlgoTraders, All rights reserved
 */

import { useEffect, useMemo, useRef, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import "./OverrideDialog.scss";

const KIND_TITLES = {
  startDate: "Set start date",
  threshold: "Override threshold",
  acknowledgment: "Acknowledge check",
  dateRangeException: "Add date-range exception",
};

const KIND_HELP = {
  startDate: "Ignore bars before this date for every check on this security.",
  threshold: "Replace the default threshold on this check for this security only.",
  acknowledgment: "Force severity to OK for this check on this security, with a reason.",
  dateRangeException: "Suppress this date range for the selected check (known corporate action, known roll, early close…).",
};

const DATE_RANGE_EXCEPTION_DEFAULT_START = "1900-01-01";

const previousBusinessDateIso = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  while (d.getDay() === 0 || d.getDay() === 6) {
    d.setDate(d.getDate() - 1);
  }
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const OverrideDialog = ({
  open,
  kind,
  securityName,
  barSize,
  checkName,
  checkDisplayName,
  thresholdOptions,
  prefillStartDate,
  prefillEndDate,
  onConfirm,
  onCancel,
}) => {
  const [reason, setReason] = useState("");
  const [value, setValue] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [parameterName, setParameterName] = useState("");
  const [error, setError] = useState("");
  const startDateInputRef = useRef(null);
  const thresholdOptionsRef = useRef(thresholdOptions);
  thresholdOptionsRef.current = thresholdOptions;

  useEffect(() => {
    if (!open) return;
    setReason("");
    setValue("");
    if (kind === "dateRangeException") {
      setStartDate(prefillStartDate || DATE_RANGE_EXCEPTION_DEFAULT_START);
      setEndDate(prefillEndDate || previousBusinessDateIso());
    } else {
      setStartDate("");
      setEndDate("");
    }
    setError("");
    const opts = thresholdOptionsRef.current;
    if (kind === "threshold" && opts && opts.length > 0) {
      setParameterName(opts[0].parameterName);
      setValue(String(opts[0].defaultValue ?? ""));
    } else {
      setParameterName("");
    }
    if (kind === "startDate") {
      const id = window.requestAnimationFrame(() => startDateInputRef.current?.focus());
      return () => window.cancelAnimationFrame(id);
    }
  }, [open, kind, prefillStartDate, prefillEndDate]);

  const title = useMemo(() => KIND_TITLES[kind] || "Override", [kind]);
  const help = KIND_HELP[kind] || "";

  const handleSubmit = (andRun = false) => {
    const payload = {
      kind,
      securityName,
      barSize,
      reason: reason.trim(),
    };
    if (kind === "startDate") {
      if (!value) {
        setError("Start date is required.");
        return;
      }
      const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (!match) {
        setError("Start date must be in yyyy-mm-dd format.");
        return;
      }
      const [, yyyy, mm, dd] = match;
      const iso = `${yyyy}-${mm}-${dd}`;
      const parsed = new Date(iso);
      if (Number.isNaN(parsed.getTime()) || parsed.getUTCDate() !== Number(dd) || (parsed.getUTCMonth() + 1) !== Number(mm)) {
        setError("Start date is not a valid calendar date.");
        return;
      }
      payload.value = iso;
    } else if (kind === "threshold") {
      if (!parameterName) {
        setError("Parameter is required.");
        return;
      }
      if (value === "" || Number.isNaN(Number(value))) {
        setError("Value must be a number.");
        return;
      }
      payload.checkName = checkName;
      payload.parameterName = parameterName;
      payload.value = value;
    } else if (kind === "acknowledgment") {
      payload.checkName = checkName;
    } else if (kind === "dateRangeException") {
      if (!startDate || !endDate) {
        setError("Both start and end dates are required.");
        return;
      }
      if (startDate > endDate) {
        setError("Start date must be on or before end date.");
        return;
      }
      payload.checkName = checkName;
      payload.startDate = startDate;
      payload.endDate = endDate;
    }
    onConfirm(payload, andRun);
  };

  const selectedThresholdHelp = useMemo(() => {
    if (kind !== "threshold" || !thresholdOptions) return "";
    const opt = thresholdOptions.find((o) => o.parameterName === parameterName);
    return opt?.description || "";
  }, [kind, thresholdOptions, parameterName]);

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      className="overrideDialog"
      fullWidth
      maxWidth="sm"
      PaperProps={{ className: "dialogPaper" }}
    >
      <DialogTitle className="dialogTitle">
        <Typography variant="h6" component="div">{title}</Typography>
      </DialogTitle>
      <DialogContent className="dialogContent">
        <Typography variant="body2" className="helperText">
          {help}
        </Typography>
        <Typography variant="body2" className="contextText">
          {securityName} · {barSize}
          {checkDisplayName ? ` · ${checkDisplayName}` : checkName ? ` · ${checkName}` : ""}
        </Typography>

        {kind === "startDate" && (
          <TextField
            className="field"
            label="Start date"
            placeholder="yyyy-mm-dd"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputRef={startDateInputRef}
            inputProps={{ inputMode: "numeric", maxLength: 10 }}
            fullWidth
            variant="outlined"
          />
        )}

        {kind === "threshold" && (
          <>
            <TextField
              className="field"
              label="Parameter"
              select
              value={parameterName}
              onChange={(e) => {
                setParameterName(e.target.value);
                const opt = thresholdOptions?.find((o) => o.parameterName === e.target.value);
                if (opt) setValue(String(opt.defaultValue ?? ""));
              }}
              fullWidth
              variant="outlined"
            >
              {(thresholdOptions || []).map((opt) => (
                <MenuItem key={opt.parameterName} value={opt.parameterName}>
                  {opt.label || opt.parameterName}
                </MenuItem>
              ))}
            </TextField>
            {selectedThresholdHelp && (
              <Typography variant="caption" className="helperText">{selectedThresholdHelp}</Typography>
            )}
            <TextField
              className="field"
              label="Value"
              type="number"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              fullWidth
              variant="outlined"
              inputProps={{ step: "any" }}
            />
          </>
        )}

        {kind === "dateRangeException" && (
          prefillStartDate && prefillEndDate && prefillStartDate === prefillEndDate ? (
            <Typography variant="body2" className="contextText">
              Suppressing single bar: {prefillStartDate}
            </Typography>
          ) : (
            <div className="dateRow">
              <TextField
                className="field"
                label="Start date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
              />
              <TextField
                className="field"
                label="End date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
              />
            </div>
          )
        )}

        <TextField
          className="field reasonField"
          label="Reason (optional)"
          placeholder="e.g. known 2020 COVID volatility window"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          multiline
          minRows={2}
          fullWidth
          variant="outlined"
          autoFocus={kind !== "startDate"}
        />
        {error && (
          <Typography variant="body2" className="errorText">{error}</Typography>
        )}
      </DialogContent>
      <DialogActions className="dialogActions">
        <Button onClick={onCancel} className="cancelButton">Cancel</Button>
        <Button
          onClick={() => handleSubmit(false)}
          className="confirmButton"
          variant="outlined"
        >
          Save override
        </Button>
        <Button
          onClick={() => handleSubmit(true)}
          className="confirmButton primary"
          variant="contained"
        >
          Save &amp; Run
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OverrideDialog;
